////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ITEM-ARR-READERS.TS — TS port (Wave 2c of plans/bundler-script-load-order-phase2.md). GENERATED from the
// byte-faithful body of includes/js/item-arr-readers.js (tools/build/_gen-item-arr-readers-ts.mjs); the ONLY
// additions are this exact Window contract + the EXHAUSTIVE window attach at the bottom.
//
// ⚠ AUTO-ATTACH TRAP: the classic <script> auto-attached every top-level function/var to window, so the
// original explicit window.* block was INCOMPLETE (ftDeriveVitality / ftStatusEffectPermanentUuids + every
// _ft* internal + FT_* table relied on it). A module does NOT auto-attach, so this port attaches EVERY
// top-level declaration — a superset of the classic surface. The core-2c and pre-core parity smokes
// assert every declared name reaches window. STAGED (not footer-swapped) pending real-browser parity.
//
// FAITHFUL-PORT MODE: runtime logic stays byte-identical to the .js. The generator adds explicit type-only
// boundary annotations/casts so noImplicitAny can guard the generated program. KEEP logic changes in the .js
// and re-run the generator; do not hand-edit below the header.
// NOTE (2026-06-29): includes/js/item-arr-readers.js is NOT dead — it stays as the canonical eval-source for
// live test/probe harnesses (tools/test/passive-tree-smoke.mjs, test-object-card-readers.cjs,
// tools/probes/probe-*.mjs). So the .js + its generator are retained (unlike the other Wave-2b ports).
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import { permanentStatusAllowed } from '../janus-engine/compile/permanent-status-guard';

import { readCreaturePayloadStatuses } from '../janus-engine/compile/creature-status-reader';

declare global {
    interface Window {
        FateTide?: {
            casterCache?: Map<string, { catalog?: Record<string, unknown> }>;
            objectCache?: Map<string, { catalog?: Record<string, unknown> }>;
            encounterCache?: Map<string, { catalog?: Record<string, unknown> }>;
        };
        ftCreatorCharacter?: { catalog?: Record<string, any> };
        ftFindFullNestedItem?: (id: string) => any;
        ftInventoryField: typeof ftInventoryField;
        ftEffectiveSizeId: typeof ftEffectiveSizeId;
        ftEncounterField: typeof ftEncounterField;
        ftEncounterCreatureField: typeof ftEncounterCreatureField;
        ftReachOf: typeof ftReachOf;
        ftItemsCol: typeof ftItemsCol;
        ftCreatureStatuses: typeof ftCreatureStatuses;
        ftCreaturePayloadStatuses: typeof ftCreaturePayloadStatuses;
        ftComputedValue: typeof ftComputedValue;
        ftItemPictures: typeof ftItemPictures;
        ftPicturesOf: typeof ftPicturesOf;
        ftPrimaryPictureOf: typeof ftPrimaryPictureOf;
        ftItemVideos: typeof ftItemVideos;
        ftHasPlayerPermission: typeof ftHasPlayerPermission;
        ftHasCampaignPermission: typeof ftHasCampaignPermission;
        ftLayerValue: typeof ftLayerValue;
        ftPosCoord: typeof ftPosCoord;
        ftLayerSlot: typeof ftLayerSlot;
        ftLayerEntries: typeof ftLayerEntries;
        ftLayerHas: typeof ftLayerHas;
        ftScalarLayerSlot: typeof ftScalarLayerSlot;
        ftLayerEntryUuid: typeof _ftLayerEntryUuid;
        ftSourceOf: typeof ftSourceOf;
        pictureField0: typeof pictureField0;
        videoField0: typeof videoField0;
    }
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ITEM-ARR-READERS.JS — vanilla-JS readers for the bucket-reshaped $item_arr / caster blob.
//
// Phase 2e of plans/template-js-carve-up.md (see [[project_template_js_refactor]]). These are the
// front-end mirrors of the PHP bucket readers (layer_value / layer_entries / computed_value /
// pictures_of / item_field) and the TS twins in includes/js-src/shared/array-types.ts — part of
// [[project_item_arr_bucket_reshape]] + [[project_classification_nest_and_walk]]. PURE array-walkers:
// no DOM, no fetch, no other template.js deps.
//
// WHY ITS OWN EARLY-LOADED FILE: these were defined in template.js but consumed heavily by the
// info-panel bundle (used 177× repo-wide, incl. ~36 in dist/info-panel-bundle.js + 8 info-panel TS
// sources) as window globals. Hoisting them here — loaded right after core-helpers.js, BEFORE every
// bundle and template.js — lets the info-panel bundle (and any future bundle) read the blob without
// reaching into the 779KB god-file. Same decoupling play as core-helpers (Phase 1).
//
// PUBLIC GLOBALS (window-exported): ftItemsCol, ftComputedValue, ftItemPictures, ftPicturesOf,
//   ftPrimaryPictureOf, ftItemVideos, ftHasPlayerPermission, ftHasCampaignPermission, ftLayerValue,
//   ftLayerSlot, ftLayerEntries. The _ft* helpers + FT_* tables are file-scope internals (still
//   window-attached via top-level function/var declarations, as they were in template.js).
//
// KEEP IN SYNC with the PHP readers + includes/js-src/shared/array-types.ts twins + the DB
// default-provider / aggregation rows.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

function ftItemsCol(arr: any, key: any): any {
    if (arr === null || arr === undefined) return undefined;
    var bucketed = arr.items != null ? arr.items[key] : undefined;
    if (bucketed !== undefined && bucketed !== null) {
        return Array.isArray(bucketed) ? bucketed[0] : bucketed;
    }
    var flat = arr[key];
    if (flat !== undefined && flat !== null) {
        return Array.isArray(flat) ? flat[0] : flat;
    }
    // Status-entry identity bridge (mirror of PHP item_field): on lean status rows
    // item_type / item_name live in the status def — ROUND-9 #11: the collapsed ref rides
    // under `status_item_id` (the honest DB column name); a bare string under status_arr is
    // the legacy v27/v28 warm-blob form (item_id IS the ref; name/type via the catalogs).
    if (
        (key === 'item_type' || key === 'item_name' || key === 'item_id')
        && (arr.status_arr != null || arr.status_item_id != null)
    ) {
        if (typeof arr.status_arr === 'object' && arr.status_arr !== null) {
            return ftItemsCol(arr.status_arr, key);
        }
        var saRef = (typeof arr.status_item_id === 'string' && arr.status_item_id !== '')
            ? arr.status_item_id
            : ((typeof arr.status_arr === 'string' && arr.status_arr !== '') ? arr.status_arr : '');
        if (saRef !== '') {
            if (key === 'item_id') return saRef;
            var saIdent = _ftCatalogIdentity(arr, saRef);
            if (saIdent != null && saIdent[key] !== undefined && saIdent[key] !== null) {
                return Array.isArray(saIdent[key]) ? saIdent[key][0] : saIdent[key];
            }
        }
        return undefined;
    }
    // Thinned template-pointer fallback (gear-template catalogize v23; ROUND-6 #1 widened):
    // inventory instances carry NO items bucket — the template id rides
    // inventory.inventory_item_id; identity/created_by live ONCE in the root `.catalog`.
    // Riding catalog first, then the ambient cache roots. Twin of the TS itemsCol fallback
    // in shared/array-types.ts + PHP item_field()'s gear-template bridge.
    var tplId = _ftInventoryTemplateIdJS(arr);
    if (tplId !== '') {
        if (key === 'item_id' && _ftIsInventoryInstanceJS(arr)) return tplId;
        if (key === 'item_name' || key === 'item_type' || key === 'flavor_text'
            || key === 'item_created_by_id' || key === 'item_description' || key === 'homebrew') {
            var ident = _ftCatalogIdentity(arr, tplId);
            if (ident != null && ident[key] !== undefined && ident[key] !== null) {
                return Array.isArray(ident[key]) ? ident[key][0] : ident[key];
            }
        }
    }
    return undefined;
}

// Catalog identity lookup for thinned template pointers (vanilla twin of the TS helper in
// shared/array-types.ts): node's riding catalog first, then the ambient cache roots' catalogs.
// Hit-memoized per id; normalizes identity entries and hoisted full defs to one shape.
var _ftCatalogIdentMemo: any = {};
function _ftCatalogIdentity(node: any, id: any): any {
    function norm(e: any): any {
        if (e == null || typeof e !== 'object') return undefined;
        return (e.items && typeof e.items === 'object') ? e.items : e;
    }
    var ride = node != null ? node.catalog : null;
    if (ride && typeof ride === 'object') {
        var n = norm(ride[id] || ride[id.toUpperCase()] || ride[id.toLowerCase()]);
        if (n) return n;
    }
    var memoK = id.toUpperCase();
    if (Object.prototype.hasOwnProperty.call(_ftCatalogIdentMemo, memoK)) return _ftCatalogIdentMemo[memoK];
    var ft = (typeof window !== 'undefined') ? window.FateTide : undefined;
    var maps = ft ? [ft.casterCache, ft.objectCache, ft.encounterCache] : [];
    for (var mi = 0; mi < maps.length; mi++) {
        var m = maps[mi];
        if (!m || typeof m.values !== 'function') continue;
        var it = m.values();
        for (var step = it.next(); !step.done; step = it.next()) {
            var root = step.value;
            var cat = (root != null && typeof root === 'object') ? root.catalog : null;
            if (!cat || typeof cat !== 'object') continue;
            var hit = norm(cat[id] || cat[id.toUpperCase()] || cat[id.toLowerCase()]);
            if (hit) { _ftCatalogIdentMemo[memoK] = hit; return hit; }
        }
    }
    return undefined;
}

// v33 (ROUND-12 #3): VitalityStatus band uuids + calculateStatus twin (additions.php).
// KEEP IN SYNC with the TS twin (shared/array-types.ts deriveVitality) and PHP
// ft_hydrate_vitality (build_item_arr.php).
var FT_VITALITY_STATUS_IDS: any = {
    invigorated:    '82788F70-EBEF-44C4-80E7-6622AD20DD60',
    fresh:          'FF30B76E-785D-2CAF-FC66-C131D0548808',
    barely_wounded: '658483AA-34DE-74E5-C974-E3E202C31CC7',
    wounded:        'C97076EF-FED9-19B4-D109-C22F9BC740E8',
    bloodied:       '44B8792E-632E-2651-C666-67DFD181E92A',
    well_bloodied:  '298BE1DD-2917-476E-D79C-92CF8C598926',
    on_deaths_door: 'D5DE14C4-3194-87BC-D767-6D45BDD90552',
    dying:          '8AE129AF-52D8-CCE8-706A-85767DB52740'
};

function _ftCalcVStatus(cur: any, max: any, temp: any): any {
    if (max <= 0) return FT_VITALITY_STATUS_IDS.dying;
    var ratio = cur / max;
    if (ratio >= 1.0) return temp > 0 ? FT_VITALITY_STATUS_IDS.invigorated : FT_VITALITY_STATUS_IDS.fresh;
    if (ratio > 0.75) return FT_VITALITY_STATUS_IDS.barely_wounded;
    if (ratio > 0.50) return FT_VITALITY_STATUS_IDS.wounded;
    if (ratio > 0.25) return FT_VITALITY_STATUS_IDS.bloodied;
    if (ratio > 0.10) return FT_VITALITY_STATUS_IDS.well_bloodied;
    if (ratio > 0.00) return FT_VITALITY_STATUS_IDS.on_deaths_door;
    return FT_VITALITY_STATUS_IDS.dying;
}

// v33 (ROUND-12 #3): lean non-living vitality buckets store base state only — derive
// the full historical shape on read. Fat blocks (v_status stored) pass through.
function ftDeriveVitality(v: any): any {
    if (v == null || typeof v !== 'object' || v.v_status !== undefined) return v || {};
    var num = function (x: any): number {
        if (Array.isArray(x)) x = x[0];
        var n = parseFloat(String(x == null ? 0 : x));
        return isNaN(n) ? 0 : n;
    };
    var baseCur = num(v.base_v_current != null ? v.base_v_current : v.v_current);
    var baseMax = num(v.base_v_max != null ? v.base_v_max : v.v_max);
    var temp    = num(v.base_v_temp != null ? v.base_v_temp : v.v_temp);
    var maxTemp = num(v.base_v_max_temp != null ? v.base_v_max_temp : v.v_max_temp);
    var effMax  = Math.max(1, baseMax + maxTemp);
    var cur     = Math.max(0, Math.min(baseCur, effMax));
    var total   = effMax + temp;
    var pct     = total > 0 ? Math.floor((cur / total) * 100) : 0;
    var out: any = {
        v_current: cur, v_max: baseMax, effective_v_max: effMax,
        v_status: _ftCalcVStatus(cur, effMax, temp),
        v_percent: pct, v_bar_v_percent: pct, v_bar_total: total
    };
    if (effMax - cur !== 0) out.v_lost = effMax - cur;
    if (temp !== 0) {
        var tPct = total > 0 ? Math.floor((temp / total) * 100) : 0;
        out.v_temp = temp; out.v_temp_percent = tPct; out.v_bar_v_temp_percent = tPct;
    }
    if (maxTemp !== 0) {
        out.v_max_temp = maxTemp;
        out.v_max_temp_percent = total > 0 ? Math.floor((maxTemp / total) * 100) : 0;
    }
    // Stored keys win (a mid-combat v_current write stays authoritative).
    for (var k in v) { if (Object.prototype.hasOwnProperty.call(v, k)) out[k] = v[k]; }
    return out;
}

function ftComputedValue(arr: any, bucket: any, key: any): any {
    if (arr === null || arr === undefined) return undefined;
    // A refused member still has saved health. This is a display read, never a compile.
    if (bucket === 'vitality' && arr._entity_refusal && !arr.computed?.creature && arr.encounter_creatures) {
        return ftDeriveVitality({
            v_current: ftEncounterCreatureField(arr.encounter_creatures, 'v_current') ?? 0,
            v_max: ftEncounterCreatureField(arr.encounter_creatures, 'v_max') ?? 0,
            v_temp: ftEncounterCreatureField(arr.encounter_creatures, 'v_temp') ?? 0,
            v_max_temp: ftEncounterCreatureField(arr.encounter_creatures, 'v_max_temp') ?? 0
        })[key];
    }
    var bucketed = (arr.computed != null && arr.computed[bucket] != null) ? arr.computed[bucket][key] : undefined;
    if (bucketed !== undefined && bucketed !== null) {
        return Array.isArray(bucketed) ? bucketed[0] : bucketed;
    }
    var flatBucket = arr[bucket];
    if (flatBucket === null || flatBucket === undefined) {
        // v33 (ROUND-12 #3): lean non-living vitality — derive the missing key.
        if (bucket === 'vitality' && arr.computed != null && arr.computed.vitality != null) {
            var dv = ftDeriveVitality(arr.computed.vitality)[key];
            if (dv !== undefined && dv !== null) return dv;
        }
        return undefined;
    }
    var flat = flatBucket[key];
    if (flat === undefined || flat === null) {
        if (bucket === 'vitality') {
            var dvf = ftDeriveVitality(flatBucket)[key];
            if (dvf !== undefined && dvf !== null) return dvf;
        }
        return undefined;
    }
    return Array.isArray(flat) ? flat[0] : flat;
}

// Shape-tolerant map-coordinate read — mirror of PHP pos_coord() and the TS
// posCoord() twin in shared/array-types.ts. The container was renamed
// pos_arr -> position and coordinates descalared from [0]-wrapped to bare
// (2026-06-02). Reads `position` (preferred) or legacy `pos_arr`, from a parent
// arr OR a bare container, unwrapping a legacy [0]. Returns a Number or
// undefined. KEEP IN SYNC with the PHP/TS twins.
function ftPosCoord(arr: any, coord: any): any {
    if (arr === null || arr === undefined) return undefined;
    var container = null;
    if (arr.position != null && typeof arr.position === 'object') {
        container = arr.position;
    } else if (arr.pos_arr != null && typeof arr.pos_arr === 'object') {
        container = arr.pos_arr;
    } else if (arr[coord] !== undefined) {
        container = arr; // bare container passed directly
    }
    if (container == null) return undefined;
    var v = container[coord];
    if (Array.isArray(v)) v = v[0];
    if (v === null || v === undefined || v === '') return undefined;
    var n = typeof v === 'number' ? v : parseFloat(String(v));
    return isNaN(n) ? undefined : n;
}

// ─────────────────────────────────────────────────────────────────────────────
// [[project_classification_nest_and_walk]] P1 (2026-05-28)
// Front-end mirror of ItemLayerTypeCatalog::isDefaultProvider /
// aggregationStrategy. Hardcoded (small/stable set; no DB read on the FE).
// Keep in sync with DB rows set by P2/P4 migrations + shared TS twins in
// includes/js-src/shared/array-types.ts.
// ─────────────────────────────────────────────────────────────────────────────
// P4 Stage B (2026-05-29): list IS the priority order — template > sub_type >
// type (30/20/10). Walker iterates in array order; first carrier with a value
// wins. Classifications stay at the bottom (priority 0) where their slots
// don't overlap with each other anyway.
var FT_DEFAULT_PROVIDER_LAYERS = [
    'creature_template',
    'creature_sub_type',
    'creature_type',
    'weapon_classification',
    'weapon_ammo_classification',
    'armor_classification',
    'instrument_classification',
    'armor_type'
];
var FT_AGGREGATION_STRATEGY: any = {
    // P4 Stage B prepend (taxonomy primary-type chain semantics):
    creature_type:     'prepend',
    creature_sub_type: 'prepend',
    creature_template: 'prepend',
    // P4 Stage B append (taxonomy stacking — racial/template additions concat after host):
    attack_ability:                            'append',
    passive_ability:                           'append',
    opportunity_attack_ability:                'append',
    ability:                                   'append',
    free_ability:                              'append',
    inventory_equipped:                        'append',
    inventory_unequipped:                      'append',
    natural_weapon:                            'append',
    natural_armor:                             'append',
    contesting_roll_adv_vs_status_effect:      'append',
    contesting_roll_dis_vs_status_effect:      'append'
    // skill_tier_* handled via prefix check in _ftAggStrategy.
};
function _ftAggStrategy(name: any): any {
    // Creature defense layers always ACCUMULATE (computed store ∪ carrier walk) — reader-side override
    // mirroring PHP layer_entries; the defense layers are exactly the FT_CREATURE_DEFENSE_SLOTS keys.
    if (FT_CREATURE_DEFENSE_SLOTS[name] !== undefined) return 'append';
    if (FT_AGGREGATION_STRATEGY[name] !== undefined) return FT_AGGREGATION_STRATEGY[name];
    if (typeof name === 'string' && name.indexOf('skill_tier_') === 0) return 'append';
    return 'host_wins';
}
function _ftLayerEntryUuid(entry: any): any {
    if (typeof entry === 'string') return entry.indexOf('|') !== -1 ? entry.split('|', 2)[0] : entry;
    if (entry === null || typeof entry !== 'object') return '';
    var bid = (entry.items && typeof entry.items.item_id === 'string') ? entry.items.item_id : '';
    if (bid !== '') return bid.indexOf('|') !== -1 ? bid.split('|', 2)[0] : bid;
    if (typeof entry.item_id === 'string' && entry.item_id !== '') return entry.item_id.indexOf('|') !== -1 ? entry.item_id.split('|', 2)[0] : entry.item_id;
    if (typeof entry.item_layer_effect === 'string' && entry.item_layer_effect !== '') return entry.item_layer_effect.indexOf('|') !== -1 ? entry.item_layer_effect.split('|', 2)[0] : entry.item_layer_effect;
    return '';
}
/**
 * Resolved status_effect_permanent UUIDs for a creature/caster blob.
 * Data-shape leanup (2026-06-10): on normalized PC casters the rows live in
 * computed.creature.abilities.status_effect_permanent (the root copy is stripped);
 * encounter-creature/monster blobs still carry root or item_layer_types rows.
 * ftLayerEntries bridges all three shapes; entries may be bare UUID strings
 * (user-choice grants) or full-nest item arrs (classification merge rows).
 */
function ftStatusEffectPermanentUuids(arr: any): any {
    var out: any[] = [];
    var entries = ftLayerEntries(arr, 'status_effect_permanent');
    for (var i = 0; i < entries.length; i++) {
        var id = _ftLayerEntryUuid(entries[i]);
        if (id !== '' && out.indexOf(id) === -1) out.push(id);
    }
    return out;
}

function _ftPcProviderSlotNames(): any {
    if ((_ftPcProviderSlotNames as any)._cache) return (_ftPcProviderSlotNames as any)._cache;
    var names = ['ancestry_1', 'ancestry_2', 'lineage', 'background'];
    var i;
    for (i = 1; i <= 20; i++) names.push('class_' + i);
    for (i = 1; i <= 20; i++) names.push('subclass_' + i);
    (_ftPcProviderSlotNames as any)._cache = names;
    return names;
}

// Resolve a CATALOGIZED carrier ref (bare-UUID string in a default-provider slot) to its full def.
// Lookup order is the owner node's riding catalog (AJAX tile mounts), the page-local creator catalog
// (saved-character island), then window.ftFindFullNestedItem (normal ambient cache roots). Keeping
// these contexts distinct is critical: an unsaved creator's class/background response owns defs that
// do not exist in window.ftCreatorCharacter.catalog. Session-memoized on hits only
// (catalog defs are static; a miss may just be an unfilled cache at boot). Re-entrancy guard:
// the finder's descent calls _ftDefaultProviderCarriers — inner calls skip string resolution.
// Twin of _resolveCarrierRef in shared/array-types.ts (Batch 3, 2026-06-11).
var _ftCarrierRefMemo: any = {};
var _ftCarrierResolveActive = false;
var _FT_CARRIER_UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
function _ftResolveCarrierRef(uuid: any, owner: any = null): any {
    if (_ftCarrierResolveActive) return null;
    var key = String(uuid).toUpperCase();
    if (Object.prototype.hasOwnProperty.call(_ftCarrierRefMemo, key)) return _ftCarrierRefMemo[key];
    if (typeof window === 'undefined') return null;
    // AJAX inventory tile mounts carry their own compiled catalog on the inventory node. Resolve
    // against that riding blob before consulting page/global stores; class/background equipment on
    // an unsaved creator has no entries in window.ftCreatorCharacter.catalog yet.
    var ridingCatalog = owner && owner.catalog;
    if (ridingCatalog && typeof ridingCatalog === 'object') {
        var ridingNode = ridingCatalog[uuid] || ridingCatalog[key] || ridingCatalog[String(uuid).toLowerCase()];
        // The creator stamps its island catalog onto each saved inventory row as the riding catalog
        // (character-creator/index.ts), so the identity-only guard below applies here too — but only to that catalog.
        var ridingIsCreator = ridingCatalog === (window.ftCreatorCharacter && window.ftCreatorCharacter.catalog);
        var ridingIsDef = !!ridingNode && typeof ridingNode === 'object'
            && ((ridingNode.items !== null && typeof ridingNode.items === 'object')
                || (ridingNode.item_layer_types !== null && typeof ridingNode.item_layer_types === 'object'));
        if (ridingNode && typeof ridingNode === 'object' && (!ridingIsCreator || ridingIsDef)) {
            _ftCarrierRefMemo[key] = ridingNode;
            return ridingNode;
        }
    }
    var creatorCatalog = window.ftCreatorCharacter && window.ftCreatorCharacter.catalog;
    if (creatorCatalog && typeof creatorCatalog === 'object') {
        var creatorNode = creatorCatalog[uuid] || creatorCatalog[key] || creatorCatalog[String(uuid).toLowerCase()];
        // The creator island also ships IDENTITY-ONLY {item_name,item_type} entries for non-carrier refs (names
        // for the side-info card, 2026-10-10). Those are never a carrier def — fall through to the walk.
        var creatorIsDef = creatorNode && typeof creatorNode === 'object'
            && ((creatorNode.items !== null && typeof creatorNode.items === 'object')
                || (creatorNode.item_layer_types !== null && typeof creatorNode.item_layer_types === 'object'));
        if (creatorIsDef) {
            _ftCarrierRefMemo[key] = creatorNode;
            return creatorNode;
        }
    }
    if (typeof window.ftFindFullNestedItem !== 'function') return null;
    // `armor_type` values (Light / Medium / Heavy / Natural) are a layerless TAXONOMY — identity-only
    // catalog rows with nothing to provide — so the global walk can only miss for them (and did, on every
    // action-bar repaint, 2026-09-10). Permanent null for that ONE provider slot type; every other provider
    // slot is a full carrier def that can nest weapons and must keep the walk (see array-types.ts twin).
    var identity = _ftCatalogIdentity(owner, uuid);
    if (identity && String(identity.item_type || '') === 'armor_type') {
        _ftCarrierRefMemo[key] = null;
        return null;
    }
    _ftCarrierResolveActive = true;
    var node = null;
    try { node = window.ftFindFullNestedItem(key) || null; } catch (e) { node = null; }
    _ftCarrierResolveActive = false;
    if (node !== null) _ftCarrierRefMemo[key] = node;
    return node;
}

function _ftDefaultProviderCarriers(arr: any): any {
    var out: any[] = [];
    if (arr === null || arr === undefined) return out;
    if (arr.item_layer_types !== null && typeof arr.item_layer_types === 'object') {
        for (var ci = 0; ci < FT_DEFAULT_PROVIDER_LAYERS.length; ci++) {
            var cname = FT_DEFAULT_PROVIDER_LAYERS[ci];
            if (cname === undefined) continue;
            var slot = arr.item_layer_types[cname];
            // Catalogized bare-UUID carrier ref (Batch 3) — resolve via the root catalog.
            if (typeof slot === 'string' && _FT_CARRIER_UUID_RE.test(slot)) {
                var resolvedSlot = _ftResolveCarrierRef(slot, arr);
                if (resolvedSlot !== null) out.push(resolvedSlot);
                continue;
            }
            if (slot === undefined || slot === null || typeof slot !== 'object') continue;
            var entries;
            // Any ARRAY slot is a multi-valued list — entries may be full nests ({items:...},
            // [[reference_armor_classification_thumbnail_default_provider]]) OR catalogized
            // bare-UUID strings (creature_sub_type lists since Batch 3). The old
            // `typeof slot[0] === 'object'` test wrapped an all-string list as ONE bogus
            // carrier, so multi-sub_type grants (a dragon's Bite/Claw/Tail natural weapons)
            // silently vanished from every aggregating read. Mirrors PHP
            // _ft_default_provider_carriers (array_key_exists(0, $slot)) + the TS twin.
            if (Array.isArray(slot)) {
                entries = slot;
            } else {
                entries = [slot];
            }
            for (var ei = 0; ei < entries.length; ei++) {
                var e = entries[ei];
                if (e !== null && typeof e === 'object') {
                    // A deduped full-nest carrier collapses to a SHELL ({items, pictures} but NO
                    // item_layer_types) because its real layers live in the root catalog
                    // (ft_dedupe_full_nests_to_shells). Treat it like a bare-UUID ref: re-resolve to
                    // the full def so the classification default-provider walk (damage_type /
                    // equipment_slot / roll_type / weapon_type off a weapon/armor classification)
                    // keeps resolving on client blobs — e.g. a creature's natural weapon whose
                    // weapon_classification ("Natural Weapon") serializes as a shell. PHP never hits
                    // this: layer_value runs on the freshly query_full_item_arr'd carrier. Falls back
                    // to the shell when the full def isn't cached.
                    var eIlt = e.item_layer_types;
                    var eIsShell = eIlt === null || eIlt === undefined || typeof eIlt !== 'object'
                        || (Array.isArray(eIlt) ? eIlt.length === 0 : Object.keys(eIlt).length === 0);
                    if (eIsShell) {
                        var eid = _ftNodeItemId(e);
                        if (eid !== null && _FT_CARRIER_UUID_RE.test(eid)) {
                            var resolvedShell = _ftResolveCarrierRef(eid, arr);
                            if (resolvedShell !== null) { out.push(resolvedShell); continue; }
                        }
                    }
                    out.push(e);
                } else if (typeof e === 'string' && _FT_CARRIER_UUID_RE.test(e)) {
                    // Multi-valued slot holding catalogized bare refs (creature_sub_type lists).
                    var resolvedEntry = _ftResolveCarrierRef(e, arr);
                    if (resolvedEntry !== null) out.push(resolvedEntry);
                }
            }
        }
    }
    // [[project_caster_arr_source_table_rebuild]]: PC providers nested in place under
    // arr.player_characters.{ancestry_1/2,lineage,background,class_1..20,subclass_1..20}.
    // Mirror of PHP ft_pc_provider_slot_names() / _ft_default_provider_carriers.
    var pc = arr.player_characters;
    if (pc !== null && typeof pc === 'object') {
        var slots = _ftPcProviderSlotNames();
        // ANCESTRY PASSIVES ARE CHOICE-GATED, NOT MEMBERSHIP (owner rule 2026-08-03: "a
        // character can only have 1 Origin from their Ancestry, even if it's mixed"). Twin of
        // PHP _ft_pc_ancestry_choices_are_resolved / _ft_carrier_without_passive_abilities.
        // An ancestry's passive_ability layer holds ALL THREE of its tiered passives
        // (requires_level 1/5/10); query_player_character_arr_inc.php picks ONE per tier off
        // ancestry_choice_1/2/3 and appends it to the caster's own bucket. Yielding the
        // ancestry carrier's raw list re-donated the passives the player did NOT choose — on a
        // mixed-ancestry PC that put BOTH Origins on the sheet and double-counted their
        // ancestral_<essence> layers.
        var pcAncestryChoicesResolved = _ftPcAncestryChoicesAreResolved(pc);
        for (var si = 0; si < slots.length; si++) {
            var slotName = slots[si];
            var pslot = pc[slotName];
            var dropPassiveBucket = pcAncestryChoicesResolved
                && (slotName === 'ancestry_1' || slotName === 'ancestry_2');
            // ROUND-5 #5: catalogized pc provider slots (bare-UUID refs) resolve to their full
            // def for the carrier descent — mirror of the item_layer_types leg (Batch 3).
            if (typeof pslot === 'string' && _FT_CARRIER_UUID_RE.test(pslot)) {
                var resolvedPcSlot = _ftResolveCarrierRef(pslot, arr);
                if (resolvedPcSlot !== null) {
                    out.push(dropPassiveBucket
                        ? _ftCarrierWithoutPassiveAbilities(resolvedPcSlot, 'ref')
                        : resolvedPcSlot);
                }
                continue;
            }
            if (pslot === null || typeof pslot !== 'object') continue;
            var pentries;
            // Same slot-vs-slot[0] fix as the item_layer_types loop above.
            if (Array.isArray(pslot) && pslot.length > 0 && typeof pslot[0] === 'object' && pslot[0] !== null
                && (pslot as any).items === undefined && (pslot as any).item_layer_types === undefined) {
                pentries = pslot;
            } else {
                pentries = [pslot];
            }
            for (var pi = 0; pi < pentries.length; pi++) {
                var pe = pentries[pi];
                if (pe !== null && typeof pe === 'object' && (pe.items !== undefined || pe.item_layer_types !== undefined)) {
                    out.push(dropPassiveBucket ? _ftCarrierWithoutPassiveAbilities(pe, 'nest') : pe);
                }
            }
        }
    }
    return out;
}

// _ftPcAncestryChoicesAreResolved — twin of PHP _ft_pc_ancestry_choices_are_resolved. True
// when all three ancestry_choice_N picks are present, i.e. the compile's tier-by-tier
// selection ran and the CHOSEN ancestry passives already sit on the caster's own bucket. A
// legacy row missing a pick skips that block, so its ancestry carrier stays the only source
// of ancestry passives and must NOT be stripped.
function _ftPcAncestryChoicesAreResolved(pc: any): any {
    var choiceSlots = ['ancestry_choice_1', 'ancestry_choice_2', 'ancestry_choice_3'];
    for (var i = 0; i < choiceSlots.length; i++) {
        var choice = pc[String(choiceSlots[i])];
        // Provider slots are nested in place post-normalize, so a saved pick may be a full
        // nest rather than the bare UUID it is at compile time.
        if (choice !== null && typeof choice === 'object') choice = _ftNodeItemId(choice);
        if (typeof choice !== 'string' || !_FT_CARRIER_UUID_RE.test(choice)) return false;
    }
    return true;
}

// _ftCarrierWithoutPassiveAbilities — twin of PHP _ft_carrier_without_passive_abilities.
// Shallow copy of a yielded carrier minus its own passive_ability layer. Memoized: this walk
// re-runs on every ftLayerValue / ftLayerEntries call, so an un-memoized clone per call is a
// hot-path cost for a value that never varies.
//
// `shape` DISCRIMINATES the two sources the same ancestry can arrive from and must stay in the
// key — a PC provider slot is a catalogized bare-UUID ref on some blobs and an in-place full
// nest on others, and those arrays are not interchangeable.
var _FT_CARRIER_NO_PASSIVES_MEMO: any = {};
function _ftCarrierWithoutPassiveAbilities(carrier: any, shape: any): any {
    var ilt = carrier && carrier.item_layer_types;
    if (ilt === null || ilt === undefined || typeof ilt !== 'object' || ilt.passive_ability === undefined) {
        return carrier;
    }
    var carrierId = _ftNodeItemId(carrier);
    var memoKey = String(carrierId) + '|' + String(shape);
    if (typeof carrierId === 'string' && carrierId !== '' && _FT_CARRIER_NO_PASSIVES_MEMO[memoKey] !== undefined) {
        return _FT_CARRIER_NO_PASSIVES_MEMO[memoKey];
    }
    var strippedIlt: any = {};
    for (var k in ilt) {
        if (Object.prototype.hasOwnProperty.call(ilt, k) && k !== 'passive_ability') strippedIlt[k] = ilt[k];
    }
    var stripped: any = {};
    for (var ck in carrier) {
        if (Object.prototype.hasOwnProperty.call(carrier, ck)) stripped[ck] = carrier[ck];
    }
    stripped.item_layer_types = strippedIlt;
    if (typeof carrierId === 'string' && carrierId !== '') _FT_CARRIER_NO_PASSIVES_MEMO[memoKey] = stripped;
    return stripped;
}

// ─────────────────────────────────────────────────────────────────────────────
// ROUND-4 "inventory instance = pure template ref" (2026-06-12) — vanilla twins of the
// array-types.ts helpers. Thinned inventory instances carry NO item_layer_types (byte-
// compare-dropped into the root-catalog template def) and may lack pictures.item + the
// presentation/thumbnail stamp rows; the bridges below resolve the catalog def instead.
// KEEP IN SYNC with _ftIsInventoryInstance/_ftIsThinnedInventoryInstance/
// _ftInventoryTemplateDef in shared/array-types.ts + the PHP instance-full resolver.
// ─────────────────────────────────────────────────────────────────────────────

function _ftIsInventoryInstanceJS(arr: any): any {
    if (arr === null || typeof arr !== 'object') return false;
    var iid = (arr.inventory && typeof arr.inventory === 'object') ? arr.inventory.inventory_id : undefined;
    if (iid === undefined || iid === null) iid = arr.inventory_id;
    if (Array.isArray(iid)) iid = iid[0];
    return typeof iid === 'string' && iid !== '';
}

// Volatile per-instance layers the thin keeps as a RESIDUAL ilt (defs are template-pure).
// KEEP IN SYNC with PHP FT_INVENTORY_VOLATILE_INSTANCE_LAYERS + the TS twin.
var _FT_INV_VOLATILE_LAYERS_JS: any = { current_item_charges: true };

// ROUND-6 #1: the TEMPLATE item_id of an inventory instance — items.item_id on warm
// (pre-v26) blobs, inventory.inventory_item_id once the pointer bucket is dropped.
// KEEP IN SYNC with PHP _ft_inventory_template_id + TS _ftInventoryTemplateId.
function _ftInventoryTemplateIdJS(arr: any): any {
    if (arr === null || typeof arr !== 'object') return '';
    var tid = (arr.items && typeof arr.items === 'object') ? arr.items.item_id : undefined;
    if (tid === undefined || tid === null) {
        tid = (arr.inventory && typeof arr.inventory === 'object') ? arr.inventory.inventory_item_id : undefined;
    }
    if (tid === undefined || tid === null) tid = arr.inventory_item_id;
    if (Array.isArray(tid)) tid = tid[0];
    return (typeof tid === 'string') ? tid : '';
}

// ROUND-11 #3 (v32): ENCHANTED inventory instance marker. KEEP IN SYNC with PHP
// _ft_inventory_instance_is_enchanted + TS _ftIsEnchantedInventoryInstance.
function _ftIsEnchantedInventoryInstanceJS(arr: any): any {
    if (arr === null || typeof arr !== 'object') return false;
    var et = (arr.inventory && typeof arr.inventory === 'object') ? arr.inventory.enchantment_template_id : undefined;
    if (et === undefined || et === null || et === '') et = arr.enchantment_template_id;
    if (Array.isArray(et)) et = et[0];
    if (typeof et === 'string' && et !== '') return true;
    return !!(arr.computed && typeof arr.computed === 'object' && arr.computed.enchantment);
}

function _ftIsThinnedInventoryInstanceJS(arr: any): any {
    if (!_ftIsInventoryInstanceJS(arr)) return false;
    if (_ftInventoryTemplateIdJS(arr) === '') return false; // ROUND-6 #1: helper-gated
    var ilt = arr.item_layer_types;
    if (ilt === null || ilt === undefined || typeof ilt !== 'object') return true;
    var allVolatile = true;
    for (var k in ilt) {
        if (Object.prototype.hasOwnProperty.call(ilt, k) && !_FT_INV_VOLATILE_LAYERS_JS[k]) { allVolatile = false; break; }
    }
    if (allVolatile) return true;
    // ROUND-11 #3 (v32): enchanted instances thin PER-LAYER — kept delta layers read in-tree
    // first; a missed key bridges to the catalog def (safe: enchant layering only adds/mutates).
    return _ftIsEnchantedInventoryInstanceJS(arr);
}

var _ftInvDefMemoJS: any = {};
function _ftInventoryTemplateDefJS(node: any): any {
    var id = _ftInventoryTemplateIdJS(node); // ROUND-6 #1: inventory.inventory_item_id carrier
    if (id === '') return null;
    function isFull(e: any): any {
        return !!(e && typeof e === 'object' && e.item_layer_types && typeof e.item_layer_types === 'object');
    }
    var ride = node != null ? node.catalog : null;
    if (ride && typeof ride === 'object') {
        var re = ride[id] || ride[id.toUpperCase()] || ride[id.toLowerCase()];
        if (isFull(re)) return re;
    }
    var memoK = id.toUpperCase();
    if (Object.prototype.hasOwnProperty.call(_ftInvDefMemoJS, memoK)) return _ftInvDefMemoJS[memoK];
    var ft = (typeof window !== 'undefined') ? window.FateTide : undefined;
    var maps = ft ? [ft.casterCache, ft.objectCache, ft.encounterCache] : [];
    for (var mi = 0; mi < maps.length; mi++) {
        var m = maps[mi];
        if (!m || typeof m.values !== 'function') continue;
        var it = m.values();
        for (var step = it.next(); !step.done; step = it.next()) {
            var root = step.value;
            var cat = (root != null && typeof root === 'object') ? root.catalog : null;
            if (!cat || typeof cat !== 'object') continue;
            var e = cat[id] || cat[id.toUpperCase()] || cat[id.toLowerCase()];
            if (isFull(e)) { _ftInvDefMemoJS[memoK] = e; return e; }
        }
    }
    var found = _ftResolveCarrierRef(id);
    if (isFull(found)) { _ftInvDefMemoJS[memoK] = found; return found; }
    return null;
}

// Best-effort item_id of a (possibly nested) node, bucket-aware + [0]-tolerant.
// Used by the carrier-recursion cycle guard in ftLayerValue / ftLayerEntries /
// ftPicturesOf: those readers recurse into every _ftDefaultProviderCarriers()
// entry, which now includes self-nesting creature_template carriers and the PC
// provider slots — an unguarded cycle there freezes the browser ("lagging out").
// Threading a per-descent `visited` set keyed by this id caps each path at one
// visit per item_id. Twin of PHP _ft_node_item_id().
function _ftNodeItemId(arr: any): any {
    if (arr === null || typeof arr !== 'object') return null;
    var id = (arr.items && arr.items.item_id !== undefined) ? arr.items.item_id : arr.item_id;
    if (Array.isArray(id)) id = id.length ? id[0] : null;
    return (id === undefined || id === null || id === '') ? null : String(id);
}

function ftItemPictures(arr: any): any {
    // Legacy entry point — prefer ftPicturesOf(arr, 'item') going forward.
    return ftPicturesOf(arr, 'item');
}

/**
 * Bucket-aware picture list read (vanilla-JS twin of TS picturesOf).
 * [[project_item_arr_bucket_reshape]] 2026-05-28: collapsed the per-host
 * picture aliases (item_pictures, encounter_creature_picture, etc.) into a
 * single `pictures` bucket sub-keyed by attached_item_type. Falls back to
 * legacy buckets for browser-cached blobs.
 */
function ftPicturesOf(arr: any, type: any, visited?: any): any {
    if (arr === null || arr === undefined) return [];
    visited = visited || {};
    var selfId = _ftNodeItemId(arr);
    if (selfId !== null) visited[selfId] = true;
    var bucket = (arr.pictures != null && typeof arr.pictures === 'object') ? arr.pictures : null;
    if (type === undefined || type === null) {
        if (!bucket) return [];
        var out: any[] = [];
        for (var k in bucket) {
            if (Object.prototype.hasOwnProperty.call(bucket, k)) {
                var v = bucket[k];
                if (Array.isArray(v)) {
                    for (var i = 0; i < v.length; i++) out.push(v[i]);
                }
            }
        }
        return out;
    }
    if (bucket && Array.isArray(bucket[type]) && bucket[type].length > 0) return bucket[type];
    var LEGACY: any = {
        item: ['item_pictures', 'item_picture'],
        encounter_creature_picture: ['encounter_creature_picture'],
        encounter_creature_presentation_picture: ['encounter_creature_presentation_picture'],
        encounter_creature_thumbnail_picture: ['encounter_creature_thumbnail_picture'],
        player_character_picture: ['player_character_picture'],
        inventory_picture: ['inventory_picture'],
        inventory_presentation_picture: ['inventory_presentation_picture'],
        inventory_thumbnail_picture: ['inventory_thumbnail_picture'],
        encounter_picture: ['encounter_picture'],
        location_picture: ['location_picture'],
        location_map: ['location_map'],
        source_header: ['source_picture'],
        source_header_picture: ['source_picture']
    };
    var legacyKeys = LEGACY[type] || [];
    for (var jj = 0; jj < legacyKeys.length; jj++) {
        var legacy = arr[legacyKeys[jj]];
        if (Array.isArray(legacy) && legacy.length > 0) return legacy;
    }
    // ROUND-4 thinned-instance bridge: dropped picture sub-keys on an inventory instance —
    // `item` (template gallery) from the catalog def; elided presentation/thumbnail STAMPS re-derive from
    // their pointer ids against the instance's own uploads or the def gallery. Scoped to these
    // three types only (`inventory_picture` uploads are per-instance, never dropped).
    if (
        (type === 'item' || type === 'inventory_presentation_picture' || type === 'inventory_thumbnail_picture')
        && _ftIsInventoryInstanceJS(arr)
        && _ftInventoryTemplateIdJS(arr) !== '' // ROUND-6 #1: items pointer bucket dropped — helper-gated
    ) {
        var r4PicDef = _ftInventoryTemplateDefJS(arr);
        // A catalog def is never itself an inventory instance. The carrier resolver can hand back an instance
        // (the row itself, when its template id matches a full cached node) — recursing into that loops forever.
        if (r4PicDef && (r4PicDef === arr || _ftIsInventoryInstanceJS(r4PicDef))) r4PicDef = null;
        if (type === 'item') {
            if (r4PicDef) {
                var r4DefPics = ftPicturesOf(r4PicDef, 'item', visited);
                if (r4DefPics.length > 0) return r4DefPics;
            }
        } else {
            // ROUND-9 #1 follow-up: the instance's own KEPT gallery copy (pictures.item — the
            // all-enchanted-template case, where no def exists) is a resolution pool too.
            // ROUND-9 sibling-stamp pool: the writer drops a stamp bucket whose rows the KEPT
            // sibling vouches (thumbnail usually duplicates the xz row exactly).
            var r4PtrCol = (type === 'inventory_presentation_picture') ? 'inventory_presentation_pic_id' : 'inventory_thumbnail_pic_id';
            var r4SibKey = (type === 'inventory_presentation_picture') ? 'inventory_thumbnail_picture' : 'inventory_presentation_picture';
            var r4Pid = ftInventoryField(arr, r4PtrCol);
            if (typeof r4Pid === 'string' && r4Pid !== '') {
                var r4Want = r4Pid.toLowerCase();
                var r4Pools = [
                    (arr.pictures && typeof arr.pictures === 'object') ? arr.pictures.inventory_picture : null,
                    (arr.pictures && typeof arr.pictures === 'object') ? arr.pictures.item : null,
                    (arr.pictures && typeof arr.pictures === 'object') ? arr.pictures[r4SibKey] : null,
                    (r4PicDef && r4PicDef.pictures && typeof r4PicDef.pictures === 'object') ? r4PicDef.pictures.item : null
                ];
                for (var rp = 0; rp < r4Pools.length; rp++) {
                    var r4Pool = r4Pools[rp];
                    if (!Array.isArray(r4Pool)) continue;
                    for (var rr = 0; rr < r4Pool.length; rr++) {
                        var r4Rid = r4Pool[rr] != null ? r4Pool[rr].picture_id : undefined;
                        if (Array.isArray(r4Rid)) r4Rid = r4Rid[0];
                        if (typeof r4Rid === 'string' && r4Rid.toLowerCase() === r4Want) return [r4Pool[rr]];
                    }
                }
                // v30: cross-item stamp art is interned as a catalog PICTURE entry —
                // {picture_url} keyed by the picture uuid; the pointer id is the reference.
                var r4Pe = _ftCatalogIdentity(arr, r4Pid);
                if (r4Pe != null && typeof r4Pe.picture_url === 'string' && r4Pe.picture_url !== '') {
                    return [{ picture_id: r4Pid, picture_url: r4Pe.picture_url }];
                }
            }
        }
    }
    // ROUND-9 #9 EC-stamp bridge (twin of PHP pictures_of): elided encounter_creature
    // presentation/thumbnail stamps re-derive from their pointer ids against the member's own galleries
    // (PC uploads / the creature item gallery). Fires on the MEMBER ROOT shape.
    var R9_EC_STAMPS: any = {
        encounter_creature_presentation_picture: 'encounter_creature_presentation_pic_id',
        encounter_creature_thumbnail_picture: 'encounter_creature_thumbnail_pic_id'
    };
    if (R9_EC_STAMPS[type] !== undefined && arr.encounter_creatures != null && typeof arr.encounter_creatures === 'object') {
        var r9Ec = arr.encounter_creatures;
        var r9Kept = (r9Ec.pictures && typeof r9Ec.pictures === 'object') ? r9Ec.pictures[type] : null;
        if (Array.isArray(r9Kept) && r9Kept.length > 0) return r9Kept;
        var r9Ptr = r9Ec[R9_EC_STAMPS[type]];
        if (Array.isArray(r9Ptr)) r9Ptr = r9Ptr[0];
        if (typeof r9Ptr === 'string' && r9Ptr !== '') {
            var r9Want = r9Ptr.toLowerCase();
            var r9Pools = [
                (arr.player_characters && arr.player_characters.pictures) ? arr.player_characters.pictures.player_character_picture : null,
                bucket ? bucket.item : null,
                (r9Ec.pictures && typeof r9Ec.pictures === 'object') ? r9Ec.pictures.encounter_creature_picture : null
            ];
            for (var r9p = 0; r9p < r9Pools.length; r9p++) {
                var r9Pool = r9Pools[r9p];
                if (!Array.isArray(r9Pool)) continue;
                for (var r9r = 0; r9r < r9Pool.length; r9r++) {
                    var r9Rid = r9Pool[r9r] != null ? r9Pool[r9r].picture_id : undefined;
                    if (Array.isArray(r9Rid)) r9Rid = r9Rid[0];
                    if (typeof r9Rid === 'string' && r9Rid.toLowerCase() === r9Want) return [r9Pool[r9r]];
                }
            }
        }
    }
    // [[project_classification_nest_and_walk]] P1: carrier walk (cycle-guarded).
    var carriers = _ftDefaultProviderCarriers(arr);
    for (var ck = 0; ck < carriers.length; ck++) {
        var cid = _ftNodeItemId(carriers[ck]);
        if (cid !== null && visited[cid]) continue;
        var cp = ftPicturesOf(carriers[ck], type, visited);
        if (cp.length > 0) return cp;
    }
    return [];
}

/**
 * First picture row across a priority list of attached_item_types.
 * Classification carrier fallback is now handled INSIDE ftPicturesOf via the
 * _ftDefaultProviderCarriers walker ([[project_classification_nest_and_walk]]
 * P1 2026-05-28), so priority only enumerates logical types here.
 */
function ftPrimaryPictureOf(arr: any, typePriority: any): any {
    if (arr === null || arr === undefined) return null;
    typePriority = typePriority || [
        'encounter_creature_picture',
        'encounter_creature_presentation_picture',
        'encounter_creature_thumbnail_picture',
        'player_character_picture',
        'inventory_picture',
        'encounter_picture',
        'location_picture',
        'item'
    ];
    for (var ii = 0; ii < typePriority.length; ii++) {
        var pics = ftPicturesOf(arr, typePriority[ii]);
        if (pics.length === 0) continue;
        for (var jj = 0; jj < pics.length; jj++) {
            if (pics[jj] && pics[jj].primary_picture) return pics[jj];
        }
        return pics[0] || null;
    }
    return null;
}

function ftItemVideos(arr: any): any {
    if (arr === null || arr === undefined) return [];
    var list = arr.item_videos != null ? arr.item_videos : arr.item_video;
    return Array.isArray(list) ? list : [];
}

// [[project_classification_nest_and_walk]] Phase 5A.1 round 5 (2026-05-29):
// Permission helpers — mirror PHP has_player_permission / has_campaign_permission
// and the TS twins in includes/js-src/shared/array-types.ts. Prefer the new
// arr.permissions bucket; fall back to legacy arr.player_permission /
// arr.campaign_permission shapes during the dual-write window.
function ftHasPlayerPermission(arr: any): any {
    if (arr === null || arr === undefined) return false;
    return arr.permissions?.player === true;
}
function ftHasCampaignPermission(arr: any): any {
    if (arr === null || arr === undefined) return false;
    return arr.permissions?.campaign === true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Computed-cluster bridges — JS mirror of the layer_value() bridges in PHP
// build_item_arr.php (essence/senses/speeds P4 Stage E-G, size/reach/extra_attack
// [[project_caster_arr_source_table_rebuild]], Phase 5A.1 pollution sweep,
// equipped 2026-06-08, campaign/encounter reshapes 2026-06-04/09).
// The compiles moved these layers OUT of item_layer_types / the top level into
// computed.creature.<cluster> (or computed.campaign / computed.encounter).
// Without the bridge, ftLayerValue misses the host slot and the default-provider
// carrier walk "resolves" the layer from a provider item's OWN catalog value —
// e.g. a PC's 'size' walked into the lineage item's Tiny instead of
// computed.creature.size.breakdown.final_size_id (the XZ-map tiny-token bug,
// 2026-06-10). Returns undefined on no-hit → caller falls through to normal
// bucket + carrier resolution. KEEP IN SYNC with PHP layer_value().
// ─────────────────────────────────────────────────────────────────────────────
var FT_ESSENCE_LAYERS: any = { body: 1, mind: 1, soul: 1, spirit: 1, fortune: 1 };
var FT_SENSE_LAYERS: any = {
    nightsight: 1, fiendsight: 1, blindsight: 1, ghostsight: 1,
    spellsight: 1, truesight: 1, tremorsense: 1, telepathy: 1
};
var FT_AC_BRIDGE_MAP: any = {
    max_body_ac_mod: 'max_body',
    natural_armor_ac_mod: 'natural_armor',
    armor_ac_mod: 'armor',
    shield_ac_mod: 'shield',
    total_ac_mod: 'total_mod',
    dr_total: 'dr',
    equipped_armor_inventory_item_id: 'equipped_armor_id',
    equipped_shield_inventory_item_id: 'equipped_shield_id',
    ec_total: 'ec',
    ac_total: 'ac',
    max_body_ac_mod_total: 'max_body',
    natural_armor_ac_mod_total: 'natural_armor',
    armor_ac_mod_total: 'armor',
    shield_ac_mod_total: 'shield',
    total_ac_mod_total: 'total_mod'
};
var FT_COMPUTED_CLUSTER_BRIDGE_MAP: any = {
    total_weight_held:                             ['weight',   'total_weight_held'],
    creature_encumbered_carrying_capacity:         ['capacity', 'encumbered'],
    creature_heavily_encumbered_carrying_capacity: ['capacity', 'heavily_encumbered'],
    creature_max_carrying_capacity:                ['capacity', 'max'],
    creature_push_drag_lift_capacity:              ['capacity', 'push_drag_lift'],
    creature_base_size:                            ['size',     'base'],
    creature_size_weight_multiplier:               ['size',     'weight_multiplier'],
    creature_physics_height:                       ['size',     'height'],
    hunger_meter_max:                              ['meters',   'hunger_max'],
    thirst_meter_max:                              ['meters',   'thirst_max'],
    // default_swimming / default_climbing / is_natural_swimmer RETIRED 2026-06-10
    // (cluster normalization #3): `derived: true` lives ON the speed object now.
    extra_attack:                                  ['combat',   'extra_attack'],
    hindered_attack_counter:                       ['combat',   'hindered_attack_counter'],
    // cr_f RETIRED 2026-06-10 (Phase 4 leanup): fractional-CR display derives from raw cr.
    total_level:                                   ['meta',     'total_level'],
    max_summons:                                   ['meta',     'max_summons'],
    arcane_threshold:                              ['spellcasting', 'arcane_threshold'],
    spellcasting_essence_mod:                      ['spellcasting', 'essence_mod']
};
var FT_EQUIPPED_BRIDGE_KEYS: any = {
    count_main_hand_equipped: 1, count_off_hand_equipped: 1,
    count_both_hands_equipped: 1, count_main_hand_weapon_equipped: 1,
    count_off_hand_weapon_equipped: 1, count_both_hands_weapon_equipped: 1,
    count_head_weapon_equipped: 1, count_armor_equipped: 1,
    count_shield_equipped: 1,
    // Hand-occupying intrinsic natural weapons (PHP build_item_arr.php `_equipped_bridge_keys`, 2026-09-02) — read by the
    // two-weapon rider (duel_weapon_attack_roll.php) and, since A2 S9 (2026-09-09), by the browser attack resolver. This
    // twin had drifted from PHP's list; the S9 packet's real Slam click read '' and answered "not armed".
    count_main_hand_natural_weapon_equipped: 1, count_off_hand_natural_weapon_equipped: 1,
    equipped_main_hand_natural_weapon_item_id: 1, equipped_off_hand_natural_weapon_item_id: 1,
    equipped_armor_inventory_id: 1, equipped_shield_inventory_id: 1
};
var FT_CAMPAIGN_COMPUTED_KEYS: any = {
    campaign_level: 1,
    current_session_number: 1,
    campaign_session_price_f: 1,
    calendar_current_date_f: 1,
    calendar_current_time_f: 1,
    calendar_current_full_date_f: 1,
    calendar_current_full_date_named_f: 1
};

/** {total:N} wrapper or bare scalar → scalar; undefined when absent/null. */
function _ftTotalOrScalar(slot: any): any {
    if (slot === null || slot === undefined) return undefined;
    if (typeof slot === 'object') {
        var t = Array.isArray(slot) ? undefined : slot.total;
        return (t === null || t === undefined) ? undefined : t;
    }
    return slot;
}

function _ftComputedLayerBridge(arr: any, layerName: any): any {
    var computed = (arr.computed != null && typeof arr.computed === 'object') ? arr.computed : null;
    if (computed === null) return undefined;
    var v;
    // Campaign + encounter reshape bridges (genuinely-computed display values).
    if (FT_CAMPAIGN_COMPUTED_KEYS[layerName] === 1
        && computed.campaign != null && typeof computed.campaign === 'object'
        && layerName in computed.campaign) {
        v = computed.campaign[layerName];
        if (v !== undefined && v !== null) return Array.isArray(v) ? v[0] : v;
    }
    if (computed.encounter != null && typeof computed.encounter === 'object'
        && layerName in computed.encounter) {
        v = computed.encounter[layerName];
        if (v !== undefined && v !== null) return Array.isArray(v) ? v[0] : v;
    }
    var cc = (computed.creature != null && typeof computed.creature === 'object') ? computed.creature : null;
    if (FT_ESSENCE_LAYERS[layerName] === 1) {
        // computed.creature.essences is canonical; dual-read the pre-move
        // computed.essences slot for in-flight cached blobs.
        var essSlot = (cc !== null && cc.essences != null && cc.essences[layerName] != null)
            ? cc.essences[layerName]
            : ((computed.essences != null && computed.essences[layerName] != null) ? computed.essences[layerName] : null);
        if (essSlot !== null && essSlot.total !== undefined && essSlot.total !== null) return essSlot.total;
    }
    // Per-cast effective AOE dimensions (PHP layer_value's computed.aoe_distance / _height / _depth bridge): the cast-local stamp
    // (Sanguine Widened, the Self-AOE size credit, the class-aura effective radius) wins over the pristine authored layer, so every
    // geometry reader sees the cast's real shape. Ability rows carry no computed.creature, so this sits before that early return.
    // Lane AB3 2026-10-08 (AB2 ask 3 — the browser expansion read the authored radius after janus.ts / class-aura-cast.ts stamped it).
    if (layerName === 'aoe_distance' || layerName === 'aoe_distance_height' || layerName === 'aoe_distance_depth') {
        var aoeSlot = computed[layerName];
        if (aoeSlot !== undefined && aoeSlot !== null && aoeSlot !== '') {
            if (typeof aoeSlot !== 'object') return aoeSlot;
            return Array.isArray(aoeSlot) || aoeSlot.total === undefined ? null : aoeSlot.total;
        }
    }
    if (cc === null) return undefined;
    var presenceDefenseSlot = FT_CREATURE_PRESENCE_DEFENSE_SLOTS[layerName];
    if (presenceDefenseSlot !== undefined
        && cc.defenses != null && typeof cc.defenses === 'object'
        && Array.isArray(cc.defenses[presenceDefenseSlot])
        && cc.defenses[presenceDefenseSlot].length > 0) {
        return 1;
    }
    if (FT_SENSE_LAYERS[layerName] === 1) {
        v = _ftTotalOrScalar(cc.senses != null ? cc.senses[layerName] : undefined);
        if (v !== undefined) return v;
    }
    if (layerName === 'teleport' || layerName.indexOf('speed_') === 0) {
        var speedKey = layerName === 'teleport' ? 'teleport' : layerName.slice(6);
        v = _ftTotalOrScalar(cc.speeds != null ? cc.speeds[speedKey] : undefined);
        if (v !== undefined) return v;
    }
    if (layerName === 'size') {
        var sid = (cc.size != null && cc.size.breakdown != null) ? cc.size.breakdown.final_size_id : undefined;
        if (sid !== undefined && sid !== null && sid !== '') return sid;
    }
    if (layerName === 'reach') {
        v = _ftTotalOrScalar(cc.reach);
        if (v !== undefined) return v;
    }
    if (layerName === 'extra_attack') {
        v = _ftTotalOrScalar(cc.attacks != null ? cc.attacks.extra_attack : undefined);
        if (v !== undefined) return v;
    }
    if (layerName === 'damage_mitigation') {
        // Compile-time total, else the persisted AC-cluster slot (cached blobs).
        v = _ftTotalOrScalar(cc.damage_mitigation);
        if (v === undefined) v = _ftTotalOrScalar(cc.ac != null ? cc.ac.mitigation : undefined);
        if (v !== undefined) return v;
    }
    if (FT_AC_BRIDGE_MAP[layerName] !== undefined) {
        v = _ftTotalOrScalar(cc.ac != null ? cc.ac[FT_AC_BRIDGE_MAP[layerName]] : undefined);
        if (v !== undefined) return v;
    }
    if (layerName === 'spellcasting_essence') {
        // Catalog SHELL ({items:{item_id,...}}) — extract item_id; legacy bare-UUID
        // essence_id slot tolerated for pre-flush cached blobs.
        var sp = cc.spellcasting;
        var slot = (sp != null) ? (sp.essence != null ? sp.essence : sp.essence_id) : undefined;
        if (slot !== undefined && slot !== null) {
            if (typeof slot === 'object') {
                var sid2 = (slot.items != null && slot.items.item_id != null) ? slot.items.item_id : slot.total;
                if (sid2 !== undefined && sid2 !== null) return sid2;
            } else {
                return slot;
            }
        }
    }
    if (layerName === 'main_weapon_damage_type') {
        // {items:{item_id,...}} SHELL — extract item_id (NOT .total). Bare UUID tolerated.
        var mw = (cc.combat != null) ? cc.combat.main_weapon_damage_type : undefined;
        if (mw !== undefined && mw !== null) {
            if (typeof mw === 'object') {
                if (mw.items != null && mw.items.item_id != null) return mw.items.item_id;
            } else {
                return mw;
            }
        }
    }
    var cluster = FT_COMPUTED_CLUSTER_BRIDGE_MAP[layerName];
    if (cluster !== undefined) {
        v = _ftTotalOrScalar(cc[cluster[0]] != null ? cc[cluster[0]][cluster[1]] : undefined);
        if (v !== undefined) return v;
    }
    if (FT_EQUIPPED_BRIDGE_KEYS[layerName] === 1 && cc.equipped != null) {
        v = cc.equipped[layerName];
        if (v !== undefined && v !== null) return v;
    }
    // Graduated IntegerLayerType families (kill-list #4, 2026-06-10): generic bridge to
    // computed.creature.modifiers[<name>] — flat compiled total (base + capped equipped),
    // replacing the retired legacy top-level write. Mirror of PHP layer_value().
    if (cc.modifiers != null && typeof cc.modifiers === 'object' && layerName in cc.modifiers) {
        v = _ftTotalOrScalar(cc.modifiers[layerName]);
        if (v !== undefined) return v;
    }
    return undefined;
}

/**
 * Bucket-aware single-valued layer read. Computed-cluster bridges first (mirror
 * of PHP layer_value()), then the source-table buckets (locations / campaigns /
 * encounters), then arr.item_layer_types[layer], then arr[layer]; unwraps arrays
 * and shells. When host has no value, descends into default-provider carriers
 * ([[project_classification_nest_and_walk]] P1 2026-05-28) — first non-null wins
 * (aggregation_strategy irrelevant for single).
 */
function ftLayerValue(arr: any, layerName: any, visited?: any): any {
    if (arr === null || arr === undefined) return undefined;
    visited = visited || {};
    var selfId = _ftNodeItemId(arr);
    if (selfId !== null) visited[selfId] = true;
    var bridged = _ftComputedLayerBridge(arr, layerName);
    if (bridged !== undefined) return bridged;
    var value;
    // Source-table buckets (2026-06-04/09 reshapes) — bare scalars; isset-gated,
    // no-ops for arrays without the bucket. Mirrors PHP layer_value() order.
    if (arr.locations != null && typeof arr.locations === 'object' && layerName in arr.locations) {
        value = arr.locations[layerName];
    } else if (arr.campaigns != null && typeof arr.campaigns === 'object' && layerName in arr.campaigns) {
        value = arr.campaigns[layerName];
    } else if (arr.encounters != null && typeof arr.encounters === 'object' && layerName in arr.encounters) {
        value = arr.encounters[layerName];
    } else {
        var bucket = (arr.item_layer_types != null && typeof arr.item_layer_types === 'object') ? arr.item_layer_types[layerName] : undefined;
        value = bucket !== undefined ? bucket : arr[layerName];
    }
    if (value === undefined || value === null || (Array.isArray(value) && value.length === 0)) {
        // ROUND-4 thinned-instance bridge: read from the catalog template def — before the
        // carrier walk, since the def's own ftLayerValue recursion covers its carriers.
        if (_ftIsThinnedInventoryInstanceJS(arr)) {
            var invDef = _ftInventoryTemplateDefJS(arr);
            if (invDef) {
                var invDefValue = ftLayerValue(invDef, layerName, visited);
                if (invDefValue !== undefined && invDefValue !== null) return invDefValue;
            }
        }
        // Host has no value (missing, null, or empty-array slot); walk carriers (cycle-guarded).
        var carriers = _ftDefaultProviderCarriers(arr);
        for (var ci = 0; ci < carriers.length; ci++) {
            var cid = _ftNodeItemId(carriers[ci]);
            if (cid !== null && visited[cid]) continue;
            var cv = ftLayerValue(carriers[ci], layerName, visited);
            if (cv !== undefined && cv !== null) return cv;
        }
        return undefined;
    }
    if (Array.isArray(value)) value = value[0];
    // Bucketed shell: { items: {item_id, ...}, item_layer_types: {...} }
    if (value !== null && typeof value === 'object' && value.items && typeof value.items.item_id === 'string') return value.items.item_id;
    // Legacy 3-field shell or full-nested with top-level item_id.
    if (value !== null && typeof value === 'object' && typeof value.item_id === 'string') return value.item_id;
    return value;
}

/** Raw bucket-or-top-level slot read for multi-valued layer iteration. Does NOT walk carriers — use ftLayerEntries for aggregation. */
// Creature defense layer key → computed.creature.defenses sub-key (data-shape kill-list #5,
// 2026-06-10). KEEP IN SYNC with PHP FT_CREATURE_DEFENSE_SLOTS (build_item_arr.php).
var FT_CREATURE_DEFENSE_SLOTS: any = {
    damage_type_resistance:    'resistance',
    damage_type_immunity:      'immune',
    damage_type_vulnerability: 'vulnerable',
    // immune_status_effect → flat status-UUID list (distinct from damage `immune`); written for own +
    // permanent grants by finalize_creature_arr.php; the append override unions it with the carrier walk.
    immune_status_effect:      'immune_status',
    // contesting_roll_*_vs_status_effect → flat UUID lists (sourced structs); bridged 2026-06-21 so
    // finalize stores them at computed.creature.defenses.<key> instead of the base layer (like immune_status).
    // (contesting_roll_*_vs_spells are scalar flags, NOT bridged here — sourced at read-time elsewhere.)
    contesting_roll_adv_vs_status_effect: 'contesting_roll_adv_vs_status_effect',
    contesting_roll_dis_vs_status_effect: 'contesting_roll_dis_vs_status_effect'
};
var FT_CREATURE_PRESENCE_DEFENSE_SLOTS: any = {
    force_move_resistance: 'force_move_resistance',
    immune_to_forced_movement: 'immune_to_forced_movement'
};
function ftLayerSlot(arr: any, layerName: any): any {
    if (arr === null || arr === undefined) return undefined;
    var bucket = (arr.item_layer_types != null && typeof arr.item_layer_types === 'object') ? arr.item_layer_types[layerName] : undefined;
    if (bucket !== undefined) return bucket;
    // Canonical creature defense store: computed.creature.defenses (root key stripped at
    // finalize). Sits AFTER pristine item_layer_types, BEFORE the legacy top-level fallback —
    // exact mirror of PHP _ft_read_layer_slot_raw.
    var defSlot = FT_CREATURE_DEFENSE_SLOTS[layerName];
    if (defSlot !== undefined
        && arr.computed && typeof arr.computed === 'object'
        && arr.computed.creature && typeof arr.computed.creature === 'object'
        && arr.computed.creature.defenses && typeof arr.computed.creature.defenses === 'object'
        && Array.isArray(arr.computed.creature.defenses[defSlot])
        && arr.computed.creature.defenses[defSlot].length > 0
    ) {
        return arr.computed.creature.defenses[defSlot];
    }
    var presenceDefSlot = FT_CREATURE_PRESENCE_DEFENSE_SLOTS[layerName];
    if (presenceDefSlot !== undefined
        && arr.computed && typeof arr.computed === 'object'
        && arr.computed.creature && typeof arr.computed.creature === 'object'
        && arr.computed.creature.defenses && typeof arr.computed.creature.defenses === 'object'
        && Array.isArray(arr.computed.creature.defenses[presenceDefSlot])
        && arr.computed.creature.defenses[presenceDefSlot].length > 0
    ) {
        return arr.computed.creature.defenses[presenceDefSlot];
    }
    // Graduated IntegerLayerType families (kill-list #4, 2026-06-10): flat compiled total
    // at computed.creature.modifiers[<name>]. Mirror of PHP _ft_read_layer_slot_raw.
    if (arr.computed && typeof arr.computed === 'object'
        && arr.computed.creature && typeof arr.computed.creature === 'object'
        && arr.computed.creature.modifiers && typeof arr.computed.creature.modifiers === 'object'
        && layerName in arr.computed.creature.modifiers
        && arr.computed.creature.modifiers[layerName] !== null
        && arr.computed.creature.modifiers[layerName] !== undefined
    ) {
        return arr.computed.creature.modifiers[layerName];
    }
    return arr[layerName];
}

// Read-time suppression of overridden abilities — JS twin of PHP _ft_suppress_overridden_abilities
// (build_item_arr.php). override_abilities() records the LOSING tiers (e.g. Living Weapon d4/d6/d8/d10
// under a held d12) in computed.creature.suppressed_ability_ids; PHP layer_entries drops them at read
// time, and so must we — otherwise the TS panes list every authored tier. Inert (one property probe)
// unless the node carries a suppression set AND the layer is an ability type.
var FT_ABILITY_TYPE_LAYERS: any = {
    passive_ability: 1, full_ability: 1, ability: 1, quick_ability: 1, attack_ability: 1,
    free_ability: 1, reaction_ability: 1, opportunity_attack_ability: 1,
    curse: 1, menta: 1, sanguine: 1, ability_choice: 1,
}; // mirror of PHP AbilityType::LIST (additions.php)
function _ftOverrideEntryAbilityId(entry: any): any {
    if (entry !== null && typeof entry === 'object') {
        if (typeof entry.ability_id === 'string' && entry.ability_id !== '') return entry.ability_id;
        return _ftLayerEntryUuid(entry) || '';
    }
    return typeof entry === 'string' ? entry : '';
}
function _ftSuppressOverriddenAbilities(arr: any, layerName: any, entries: any): any {
    if (!entries || entries.length === 0) return entries;
    if (layerName === 'status_effect_permanent') return entries.filter((entry: any) => permanentStatusAllowed(_ftLayerEntryUuid(entry)));
    if (!FT_ABILITY_TYPE_LAYERS[layerName]) return entries;
    var s = (arr && arr.computed && typeof arr.computed === 'object'
        && arr.computed.creature && typeof arr.computed.creature === 'object')
        ? arr.computed.creature.suppressed_ability_ids
        : null;
    if (!s || typeof s !== 'object') return entries;
    var kept = [];
    for (var i = 0; i < entries.length; i++) {
        var aid = _ftOverrideEntryAbilityId(entries[i]);
        if (aid !== '' && s[aid]) continue;
        kept.push(entries[i]);
    }
    return kept;
}

/**
 * Multi-valued layer read with carrier walk + aggregation strategy.
 * [[project_classification_nest_and_walk]] P1 2026-05-28 — mirror of PHP layer_entries.
 * Returns a list of entries; respects per-layer FT_AGGREGATION_STRATEGY
 * (host_wins / prepend / append) for combining host + carrier entries.
 * Overridden ability tiers are dropped at read time (suppression set above),
 * matching PHP layer_entries.
 */
function ftLayerEntries(arr: any, layerName: any, visited?: any): any {
    if (arr === null || arr === undefined) return [];
    visited = visited || {};
    var selfId = _ftNodeItemId(arr);
    if (selfId !== null) visited[selfId] = true;
    // [[project_caster_arr_source_table_rebuild]]: PC gated abilities/spells are DERIVED
    // data living in computed.creature.abilities[<type>] (and computed.creature.{spells_known,
    // curses_known}). Return them DIRECTLY, bypassing the host + provider-carrier aggregation
    // below — the compile already gated/curated them, so we must NOT let the provider crawl
    // re-add raw, ungated provider abilities (e.g. an over-level class-catalog ability nested
    // under player_characters.class_N). Exact mirror of PHP layer_entries()
    // (build_item_arr.php). Fires only when the computed bucket exists (PC casters
    // post-finalize); inert for creatures / items (root + carrier fallback below).
    var _ftCC = (arr.computed && typeof arr.computed === 'object' && arr.computed.creature && typeof arr.computed.creature === 'object') ? arr.computed.creature : null;
    if (_ftCC) {
        if (_ftCC.abilities && typeof _ftCC.abilities === 'object' && Array.isArray(_ftCC.abilities[layerName])) {
            return _ftSuppressOverriddenAbilities(arr, layerName, _ftCC.abilities[layerName]);
        }
        if ((layerName === 'spells_known' || layerName === 'curses_known') && Array.isArray(_ftCC[layerName])) {
            return _ftCC[layerName];
        }
    }
    var raw = ftLayerSlot(arr, layerName);
    var hostEntries;
    if (raw === undefined || raw === null || raw === '') {
        hostEntries = [];
    } else if (Array.isArray(raw)) {
        hostEntries = raw.length === 0 ? [] : raw;
    } else if (typeof raw === 'object') {
        hostEntries = [raw];
    } else {
        hostEntries = [raw];
    }
    // ROUND-4 thinned-instance bridge: delegate the whole read to the catalog template def —
    // reproduces pre-drop semantics (host entries + the def's carrier aggregation). Only when
    // the host (incl. legacy flat keys) had nothing, mirroring PHP layer_entries().
    if (hostEntries.length === 0 && _ftIsThinnedInventoryInstanceJS(arr)) {
        var r4Def = _ftInventoryTemplateDefJS(arr);
        if (r4Def) return ftLayerEntries(r4Def, layerName, visited);
    }
    var strategy = _ftAggStrategy(layerName);
    if (strategy === 'host_wins') {
        if (hostEntries.length > 0) return _ftSuppressOverriddenAbilities(arr, layerName, hostEntries);
        var carriers = _ftDefaultProviderCarriers(arr);
        for (var ci = 0; ci < carriers.length; ci++) {
            var cid = _ftNodeItemId(carriers[ci]);
            if (cid !== null && visited[cid]) continue;
            var ce = ftLayerEntries(carriers[ci], layerName, visited);
            if (ce.length > 0) return _ftSuppressOverriddenAbilities(arr, layerName, ce);
        }
        return [];
    }
    // prepend / append: dedupe-by-UUID aggregate.
    // DEFENSE-AWARE UID (mirror of PHP build_item_arr.php): the structured damage-defense rows
    // baked into computed.creature.defenses key the UUID under 'damage_type', which
    // _ftLayerEntryUuid does NOT read — so a carrier defense materialized by compute_defenses
    // must dedupe against the read-time carrier re-walk by damage_type, else it doubles.
    var _isDefSlot = FT_CREATURE_DEFENSE_SLOTS[layerName] !== undefined;
    var _entryUid = function (e: any) {
        if (_isDefSlot && e !== null && typeof e === 'object' && typeof e.damage_type === 'string') return e.damage_type;
        return _ftLayerEntryUuid(e);
    };
    var seen: any = {};
    var result = [];
    for (var hi = 0; hi < hostEntries.length; hi++) {
        var hu = _entryUid(hostEntries[hi]);
        if (hu !== '' && seen[hu]) continue;
        if (hu !== '') seen[hu] = true;
        result.push(hostEntries[hi]);
    }
    var carriers2 = _ftDefaultProviderCarriers(arr);
    for (var ck = 0; ck < carriers2.length; ck++) {
        var cid2 = _ftNodeItemId(carriers2[ck]);
        if (cid2 !== null && visited[cid2]) continue;
        var ce2 = ftLayerEntries(carriers2[ck], layerName, visited);
        for (var ei = 0; ei < ce2.length; ei++) {
            var uu = _entryUid(ce2[ei]);
            if (uu !== '' && seen[uu]) continue;
            if (uu !== '') seen[uu] = true;
            if (strategy === 'prepend') {
                result.unshift(ce2[ei]);
            } else {
                result.push(ce2[ei]);
            }
        }
    }
    return _ftSuppressOverriddenAbilities(arr, layerName, result);
}


/**
 * Front-end twin of PHP creature_statuses(). Returns the carrier's status list
 * for the given perspective from the bucketed container:
 *   'target' — statuses applied TO the carrier   (arr.statuses.target)
 *   'caster' — statuses the carrier maintains     (arr.statuses.caster)
 * Always returns an array (a lone PHP-listish row becomes a one-element list; a PHP
 * list json_encode emitted as an OBJECT — every key numeric — becomes its rows in key order). The legacy top-level status_effect / status_effect_caster keys were
 * retired 2026-06-02.
 *
 * KEEP IN SYNC with PHP creature_statuses() (includes/functions/janus/statuses/creature_statuses.php).
 */
function ftCreatureStatuses(arr: any, mode: any): any {
    if (arr === null || arr === undefined) return [];
    mode = (mode === 'caster') ? 'caster' : 'target';
    var val = (arr.statuses != null && typeof arr.statuses === 'object') ? arr.statuses[mode] : null;
    if (val === null || val === undefined) return [];
    var list = Array.isArray(val) ? val : null;
    // 2026-10-16 (FT3D Priest Guy): a PHP list whose keys went out of order json_encodes as an OBJECT ({"0":…,"2":…,"1":…}) — every key
    // numeric → its rows in key order (an empty object holds none). Any other object is a lone row.
    if (list === null && typeof val === 'object') {
        var vk: string[] = Object.keys(val);
        if (vk.every(function (k: string) { return /^\d+$/.test(k); }))
            list = vk.sort(function (a: string, b: string) { return Number(a) - Number(b); }).map(function (k: string) { return val[k]; });
    }
    if (list === null) list = [val];
    // ROUND-7 #8 (2026-06-12): cached rows carry the status def as a bare-UUID catalog REF (the
    // v21 inline def collapsed). Resolve at this single accessor — on SHALLOW COPIES so the
    // cached blob is never mutated — and every downstream consumer keeps its pre-collapse
    // shape. Unresolvable refs degrade to a minimal identity shell (layer reads return null).
    // ROUND-9 #11: the ref rides under `status_item_id`; a bare string under status_arr is the
    // legacy v27/v28 warm-blob form. The resolved def is presented under status_arr either way.
    var out: any[] = [];
    for (var si = 0; si < list.length; si++) {
        var row = list[si];
        var rowRef = '';
        if (row !== null && typeof row === 'object' && (row.status_arr == null || typeof row.status_arr === 'string')) {
            if (typeof row.status_item_id === 'string' && row.status_item_id !== '') {
                rowRef = row.status_item_id;
            } else if (typeof row.status_arr === 'string' && row.status_arr !== '') {
                rowRef = row.status_arr;
            }
        }
        // ROUND-12 #5 (v33): self-cast rows elide status_caster_id at intern (== target) —
        // re-stamp on the returned shallow copy (a stored EMPTY caster id passes through).
        var needCaster = row !== null && typeof row === 'object'
            && !('status_caster_id' in row) && typeof row.status_target_id === 'string' && row.status_target_id !== '';
        if (rowRef !== '' || needCaster) {
            var patched: any = {};
            for (var rk in row) {
                if (Object.prototype.hasOwnProperty.call(row, rk)) patched[rk] = row[rk];
            }
            if (needCaster) patched.status_caster_id = row.status_target_id;
            if (rowRef !== '') {
                var def = _ftStatusDefJS(arr, rowRef);
                patched.status_arr = def || { items: { item_id: rowRef } };
            }
            out.push(patched);
        } else {
            out.push(row);
        }
    }
    return out;
}

/**
 * Front-end twin of PHP creature_payload_statuses(). Payload consumers skip the
 * source anchor that an opposing-team-only follow aura stamps on its own caster.
 * Structural/status-identity consumers keep using lossless ftCreatureStatuses().
 */
function ftCreaturePayloadStatuses(arr: any): any[] {
    const encounterId = String(ftEncounterCreatureField(arr?.encounter_creatures, 'encounter_id') ?? '').toUpperCase();
    const held: any = window.FateTide?.encounterCache?.get(encounterId);
    const roster = held?.encounter_creatures_arr ?? arr?.encounter_arr?.encounter_creatures_arr;
    const rows = Array.isArray(roster) ? roster : Object.values(roster ?? {});
    return Object.values(readCreaturePayloadStatuses(arr, ftLayerValue,
        id => _ftStatusDefJS(arr, id), id => {
            const key = id.toUpperCase();
            const row = rows.find((candidate: any) => String(ftEncounterCreatureField(candidate?.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase() === key);
            return row ?? window.FateTide?.casterCache?.get(key) ?? null;
        }));
}

// ROUND-7 #8: full status def for a collapsed status_arr ref — the carrier's riding catalog
// (caster roots), then the ambient cache roots' catalogs, then the session carrier-ref
// re-resolver. Full defs memoized (static by the catalog invariant); an identity-only entry
// is returned un-memoized (name/type render; a later full def can still win).
var _ftStatusDefMemoJS: any = {};
function _ftStatusDefJS(carrier: any, id: any): any {
    // "Def" for a status = items identity + layers AND/OR a computed bucket (LAYERLESS
    // templates like Awake store {items, computed} — looser than the inventory-def predicate).
    function isFullDef(e: any): any {
        return !!(e && typeof e === 'object' && e.items
            && (e.item_layer_types || e.computed));
    }
    var ride = (carrier !== null && typeof carrier === 'object') ? carrier.catalog : null;
    if (ride && typeof ride === 'object') {
        var re = ride[id] || ride[id.toUpperCase()] || ride[id.toLowerCase()];
        if (isFullDef(re)) return re;
    }
    var memoK = id.toUpperCase();
    if (Object.prototype.hasOwnProperty.call(_ftStatusDefMemoJS, memoK)) return _ftStatusDefMemoJS[memoK];
    var ft = (typeof window !== 'undefined') ? window.FateTide : undefined;
    var maps = ft ? [ft.casterCache, ft.objectCache, ft.encounterCache] : [];
    for (var mi = 0; mi < maps.length; mi++) {
        var m = maps[mi];
        if (!m || typeof m.values !== 'function') continue;
        var it = m.values();
        for (var step = it.next(); !step.done; step = it.next()) {
            var root = step.value;
            var cat = (root !== null && typeof root === 'object') ? root.catalog : null;
            if (!cat || typeof cat !== 'object') continue;
            var e = cat[id] || cat[id.toUpperCase()] || cat[id.toLowerCase()];
            if (isFullDef(e)) { _ftStatusDefMemoJS[memoK] = e; return e; }
        }
    }
    // A fetched definition is its own objectCache root, keyed by id — O(1), no walk (2026-09-10).
    var objectCache = ft ? ft.objectCache : null;
    if (objectCache && typeof objectCache.get === 'function') {
        var fetched = objectCache.get(id) || objectCache.get(memoK) || objectCache.get(id.toLowerCase());
        if (isFullDef(fetched)) { _ftStatusDefMemoJS[memoK] = fetched; return fetched; }
    }
    // Identity-only catalog entry (2026-09-10): the catalog IS the def store (Round 8 — an in-tree
    // full def of a catalogizable type is an audited leak), so a status the catalogs know only as a
    // shell is a LAYERLESS template (Awake, Fully Rested: {items, computed}) or a deduped shell, and
    // the nested walk can only miss. It missed on every echo, once per cached root's version bump,
    // walking a 1,141-row encounter blob each time. Answer the identity without walking; a full def
    // that arrives later lands in objectCache (above) or a root catalog (the loop above) and wins,
    // because this answer is deliberately NOT memoized.
    var ident = _ftCatalogIdentity(carrier, id);
    if (ident) {
        return { items: { item_id: id, item_name: ident.item_name, item_type: ident.item_type } };
    }
    // An id no catalog knows at all: the nested walk is the last resort (a non-catalogized leak).
    var found = _ftResolveCarrierRef(id);
    if (isFullDef(found)) { _ftStatusDefMemoJS[memoK] = found; return found; }
    return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// CARD-SCOPED READERS (object-card TS conversion) — JS twins of the PHP helpers
// the generic item card (options/tables/table_item_object_inc.php) reads through.
// KEEP IN SYNC with their PHP twins + the array-types.ts mirrors.
// ─────────────────────────────────────────────────────────────────────────────

// Local UUID test so these readers don't depend on template.js load order
// (this file loads right after core-helpers.js, before template.js).
var _FT_UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
function _ftIsUuid(s: any): any {
    return typeof s === 'string' && _FT_UUID_RE.test(s.trim());
}

// JS twin of PHP layer_scoring_token_from_entry() (layer_cell_helpers.php): a layer
// cell's scalar token (item_layer_effect on a structured row, else [0], else scalar).
function _ftScoringTokenFromEntry(value: any): any {
    if (typeof value === 'string' || typeof value === 'number') {
        var s = String(value).trim();
        return s === '' ? null : s;
    }
    if (value !== null && typeof value === 'object') {
        if (Object.prototype.hasOwnProperty.call(value, 'item_layer_effect')) {
            var e = String(value.item_layer_effect != null ? value.item_layer_effect : '').trim();
            return e === '' ? null : e;
        }
        if (typeof value[0] === 'string' || typeof value[0] === 'number') {
            var z = String(value[0]).trim();
            return z === '' ? null : z;
        }
    }
    return null;
}

// JS twin of PHP item_arr_uuid_slot(): a single UUID from a string or structured layer row.
function _ftUuidSlot(cell: any): any {
    if (cell === null || cell === '' || cell === undefined) return '';
    if (typeof cell === 'string') {
        var s = cell.trim();
        return _ftIsUuid(s) ? s : '';
    }
    if (typeof cell === 'object') {
        var tok = _ftScoringTokenFromEntry(cell);
        if (tok !== null && _ftIsUuid(tok)) return tok;
        var u = _ftLayerEntryUuid(cell);
        return (u !== '' && _ftIsUuid(u)) ? u : '';
    }
    return '';
}

// JS twin of PHP item_arr_scalar_layer_slot(): a layer cell as a single display string
// for icon_map / openItemInfo / HTML attrs — UUID from a structured row, else scoring
// token, else scalar. The arg is the SLOT VALUE (e.g. ftLayerValue(arr, 'creature_role')),
// matching the PHP signature. KEEP IN SYNC.
function ftScalarLayerSlot(slot: any): any {
    if (slot === null || slot === '' || slot === undefined) return '';
    if (typeof slot === 'object') {
        var id = _ftUuidSlot(slot);
        if (id !== '') return id;
        var tok = _ftScoringTokenFromEntry(slot);
        return tok !== null ? tok : '';
    }
    if (typeof slot === 'string' || typeof slot === 'number') {
        return String(slot);
    }
    return '';
}

// JS twin of PHP layer_has(): true if the layer resolves to a value OR has entries.
// Mirrors layer_value(...)!==null || !empty(layer_entries(...)). A value of 0 / '' /
// false still counts as "has" (matches PHP's `!== null`). KEEP IN SYNC.
function ftLayerHas(arr: any, layerName: any): any {
    if (arr === null || arr === undefined) return false;
    var v = ftLayerValue(arr, layerName);
    if (v !== undefined && v !== null) return true;
    return ftLayerEntries(arr, layerName).length > 0;
}

// JS twin of PHP source_of(): the source bucket (items.source preferred, legacy source),
// or null. KEEP IN SYNC.
function ftSourceOf(arr: any): any {
    if (arr === null || arr === undefined) return null;
    if (arr.items != null && arr.items.source != null && typeof arr.items.source === 'object') {
        return arr.items.source;
    }
    if (arr.source != null && typeof arr.source === 'object') {
        return arr.source;
    }
    return null;
}

// ftInventoryField — JS twin of PHP inventory_field(). Reads `inventory`-table columns from the
// source-table bucket (descalared bare) with a legacy top-level fallback (bare or [0]-wrapped) for
// pre-reshape cache blobs. KEEP IN SYNC with inventory_field() + inventoryField() in array-types.ts.
function ftInventoryField(arr: any, key: any): any {
    if (arr === null || arr === undefined) return undefined;
    var bucket = arr.inventory;
    if (bucket != null && typeof bucket === 'object' && !Array.isArray(bucket) && bucket[key] !== undefined) {
        var v = bucket[key];
        return Array.isArray(v) ? v[0] : v;
    }
    var flat = arr[key];
    if (flat === undefined || flat === null) return undefined;
    return Array.isArray(flat) ? flat[0] : flat;
}


// ftEffectiveSizeId — the size an INVENTORY INSTANCE actually is.
//
// ⛔ `ftLayerValue(arr,'size')` returns the ITEM TEMPLATE's size and is WRONG for any instance
//    carrying a per-instance override. That gap became visible 2026-09-04 when the door/window
//    size-variant collapse re-pointed 197 rows onto their base item and moved the real size into
//    `inventory.inventory_size`: a Large stone door began rendering as a Medium standee everywhere
//    except the FT3D runtime, which was the ONLY consumer of the column.
//
// NULL/absent means INHERIT, so the template layer stays the fallback and every standee-only or
// un-overridden row behaves exactly as before. Use this anywhere a size drives PRESENTATION
// (standee width, object-card size icon, list display). Rules-truth consumers that deliberately
// want the template's own size should keep calling ftLayerValue directly.
function ftEffectiveSizeId(arr: any): any {
    var instance = ftInventoryField(arr, 'inventory_size');
    if (instance !== undefined && instance !== null && instance !== '') return instance;
    return ftLayerValue(arr, 'size');
}


// ftEncounterField / ftEncounterCreatureField — JS twins of encounterField() /
// encounterCreatureField() in array-types.ts. The 2026-06-04/05 reshape descalared two
// pure-row tables (encounters, encounter_creatures); their own-row columns are now bare
// scalars at the container's top level. Legacy `arr.col?.[0]` returns the first CHAR
// (descalar trap). These read bare and still unwrap a [0] from a rollover cache blob.
// ftEncounterField reads off the encounter arr; ftEncounterCreatureField reads off the
// INNER `encounter_creatures` object (pass entity.encounter_creatures).
function ftEncounterField(arr: any, key: any): any {
    if (arr === null || arr === undefined) return undefined;
    // Bucket reshape (2026-06-09): encounters-table cols moved under the `encounters` bucket. Read
    // it first, then fall back to the legacy flat top-level slot (warm cached blobs).
    var bucket = arr.encounters;
    var v = (bucket !== null && bucket !== undefined && typeof bucket === 'object' && key in bucket)
        ? bucket[key]
        : arr[key];
    if (v === undefined || v === null) return undefined;
    if (Array.isArray(v)) v = v[0];
    // Catalog shell unwrap: {items:{item_id,...}} (env / light / weather / location_type) -> UUID.
    if (v !== null && typeof v === 'object' && v.items && typeof v.items.item_id === 'string') {
        return v.items.item_id;
    }
    return v;
}
function ftEncounterCreatureField(ec: any, key: any): any {
    if (ec === null || ec === undefined) return undefined;
    var v = ec[key];
    if (v === undefined || v === null) return undefined;
    if (Array.isArray(v)) v = v[0];
    // Catalog shell unwrap — PC-path ft_shell_keys_deep() shells team / preferred_speed_type /
    // encounter_weather / encounter_environment into {items:{item_id,...}} carriers; monster ECs
    // keep bare UUIDs. Return the UUID either way (twin of encounterCreatureField in array-types.ts).
    if (v !== null && typeof v === 'object' && v.items && typeof v.items.item_id === 'string') {
        return v.items.item_id;
    }
    return v;
}
// ftReachOf — JS twin of reachOf() in array-types.ts. Reach moved off top-level to
// computed.creature.reach ({total:N} or bare) 2026-05-30; legacy top-level fallback.
function ftReachOf(arr: any): any {
    if (arr === null || arr === undefined) return undefined;
    var c = arr.computed && arr.computed.creature ? arr.computed.creature.reach : undefined;
    var v = (c !== null && c !== undefined && typeof c === 'object') ? c.total : c;
    if (v === undefined || v === null) {
        var legacy = arr.reach;
        v = Array.isArray(legacy) ? legacy[0] : legacy;
    }
    if (v === undefined || v === null || v === '') return undefined;
    var n = typeof v === 'number' ? v : parseFloat(String(v));
    return isNaN(n) ? undefined : n;
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// WINDOW EXPORTS — the public reader surface (info-panel bundle + template.js + PHP consume these).
window.ftInventoryField = ftInventoryField;
window.ftEffectiveSizeId = ftEffectiveSizeId;
window.ftEncounterField = ftEncounterField;
window.ftEncounterCreatureField = ftEncounterCreatureField;
window.ftReachOf = ftReachOf;
window.ftItemsCol = ftItemsCol;
window.ftCreatureStatuses = ftCreatureStatuses;
window.ftCreaturePayloadStatuses = ftCreaturePayloadStatuses;
window.ftComputedValue = ftComputedValue;
window.ftItemPictures = ftItemPictures;
window.ftPicturesOf = ftPicturesOf;
window.ftPrimaryPictureOf = ftPrimaryPictureOf;
window.ftItemVideos = ftItemVideos;
window.ftHasPlayerPermission = ftHasPlayerPermission;
window.ftHasCampaignPermission = ftHasCampaignPermission;
window.ftLayerValue = ftLayerValue;
window.ftPosCoord = ftPosCoord;
window.ftLayerSlot = ftLayerSlot;
window.ftLayerEntries = ftLayerEntries;
// Card-scoped readers (object-card TS conversion).
window.ftLayerHas = ftLayerHas;
window.ftScalarLayerSlot = ftScalarLayerSlot;
window.ftLayerEntryUuid = _ftLayerEntryUuid;
window.ftSourceOf = ftSourceOf;


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// PICTURE / VIDEO ROW FIELD READERS (Phase 2g, 2026-06-01) — moved here from template.js's DOM UTILITIES
// junk-drawer. PHP build_picture_arr / build_video_arr emit descalared rows; these unwrap one field.
/** PHP build_picture_arr JSON encodes each column as a one-element array; unwrap for JS. */
// Phase 2.5e (2026-05-23): tightened to descalar-only. Picture/video entries are now
// stored as direct scalars per the table-of-origin rule; wrapper tolerance removed
// to keep the data contract single-shape and surface any future shape regression.
// Legacy key aliases (old -> new). The pictures columns were renamed for clarity
// (2026-06-02): pic_id -> picture_id, ext -> picture_extension, pic_thumbnail/
// pic_number -> picture_*, attached_* -> picture_attached_*, grid_* -> map_grid_*.
// Old call sites that still pass the bare key, and pre-rename cached blobs that
// still CONTAIN the old key, both keep working: resolve to the canonical new key,
// falling back to the old key for un-rolled cached blobs. Retire once all consumers
// are migrated and caches have rolled (same lifecycle as the pic_url alias).
var PICTURE_LEGACY_KEY_ALIAS: any = {
    pic_id: 'picture_id',
    ext: 'picture_extension',
    pic_thumbnail: 'picture_thumbnail',
    pic_number: 'picture_number',
    attached_item_type: 'picture_attached_item_type',
    reference_attached_item_type: 'picture_reference_attached_item_type',
    attached_id: 'picture_attached_id',
    grid_shape: 'map_grid_shape',
    grid_color: 'map_grid_color',
    grid_hex_size: 'map_grid_hex_size',
    grid_width: 'map_grid_width',
    grid_opacity: 'map_grid_opacity',
    grid_offset_x: 'map_grid_offset_x',
    grid_offset_y: 'map_grid_offset_y',
    grid_columns: 'map_grid_columns',
    grid_rows: 'map_grid_rows',
};

function pictureField0(pic: any, key: any): any {
    if (!pic) return undefined;
    // The url field was renamed pic_url -> picture_url. 'pic_url' is still accepted
    // as a LEGACY KEY ALIAS so existing call sites resolve without churn, but it
    // always returns the canonical picture_url -- the pic_url data fallback has been
    // retired now that all producers emit picture_url. Mirror of PHP pic_field().
    if (key === 'picture_url' || key === 'pic_url') {
        return pic.picture_url ?? undefined;
    }
    var aliased = PICTURE_LEGACY_KEY_ALIAS[key];
    if (aliased !== undefined) {
        return pic[aliased] ?? pic[key] ?? undefined;
    }
    return pic[key] ?? undefined;
}

function videoField0(vidRow: any, key: any): any {
    if (!vidRow) return undefined;
    return vidRow[key] ?? undefined;
}

window.pictureField0 = pictureField0;
window.videoField0 = videoField0;


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// EXHAUSTIVE WINDOW ATTACH (generated) — every top-level declaration, so the module surface is a superset of
// the classic-script auto-attach. (The original in-body window.* statements above are kept; this re-attaches
// them plus the auto-attach-only symbols.) Verified by the core-2c and pre-core parity smokes.
Object.assign(window, {
    ftItemsCol, _ftCatalogIdentity, _ftCalcVStatus, ftDeriveVitality, ftComputedValue, ftPosCoord, _ftAggStrategy, _ftLayerEntryUuid, ftStatusEffectPermanentUuids, _ftPcProviderSlotNames, _ftResolveCarrierRef, _ftDefaultProviderCarriers, _ftIsInventoryInstanceJS, _ftInventoryTemplateIdJS, _ftIsEnchantedInventoryInstanceJS, _ftIsThinnedInventoryInstanceJS, _ftInventoryTemplateDefJS, _ftNodeItemId, ftItemPictures, ftPicturesOf, ftPrimaryPictureOf, ftItemVideos, ftHasPlayerPermission, ftHasCampaignPermission, _ftTotalOrScalar, _ftComputedLayerBridge, ftLayerValue, ftLayerSlot, _ftOverrideEntryAbilityId, _ftSuppressOverriddenAbilities, ftLayerEntries, ftCreatureStatuses, ftCreaturePayloadStatuses, _ftStatusDefJS, _ftIsUuid, _ftScoringTokenFromEntry, _ftUuidSlot, ftScalarLayerSlot, ftLayerHas, ftSourceOf, ftInventoryField, ftEffectiveSizeId, ftEncounterField, ftEncounterCreatureField, ftReachOf, pictureField0, videoField0, _ftCatalogIdentMemo, FT_VITALITY_STATUS_IDS, FT_DEFAULT_PROVIDER_LAYERS, FT_AGGREGATION_STRATEGY, _ftCarrierRefMemo, _ftCarrierResolveActive, _FT_CARRIER_UUID_RE, _FT_INV_VOLATILE_LAYERS_JS, _ftInvDefMemoJS, FT_ESSENCE_LAYERS, FT_SENSE_LAYERS, FT_AC_BRIDGE_MAP, FT_COMPUTED_CLUSTER_BRIDGE_MAP, FT_EQUIPPED_BRIDGE_KEYS, FT_CAMPAIGN_COMPUTED_KEYS, FT_CREATURE_DEFENSE_SLOTS, FT_CREATURE_PRESENCE_DEFENSE_SLOTS, _ftStatusDefMemoJS, _FT_UUID_RE, FT_ABILITY_TYPE_LAYERS, PICTURE_LEGACY_KEY_ALIAS,
});

export {};
